/**
 * API client for FastAPI backend
 */

const API_BASE = process.env.NEXT_PUBLIC_API_URL || "";

// Types

export type UserRole = "user" | "enterprise" | "admin";

export interface User {
  id: number;
  email: string;
  role: UserRole;
  experiment_bucket: string | null;
}


export interface FeedbackConfig {
  experiment_bucket: string | null;
  available_actions: string[];
}

export interface ChatFeedback {
  id: number;
  convo_id: number;
  user_id: number;
  turn_index: number;
  thumbs: string | null;
  edit_original: string | null;
  edit_revised: string | null;
  experiment_bucket: string | null;
  available_actions: string[] | null;
  created_at: string;
}

export interface ChatComment {
  id: number;
  convo_id: number;
  user_id: number;
  turn_index: number;
  text: string;
  experiment_bucket: string | null;
  created_at: string;
}


export interface Task {
  id: number;
  name: string;
  display_name: string;
  vlmeval_data: string;
  description: string | null;
  primary_metric_key: string;
  dataset_version: string | null;
  num_examples: number | null;
  paper_url: string | null;
  run_count?: number;
}

export interface Model {
  id: number;
  name: string;
  display_name: string;
  vlmeval_model: string;
  model_type: string;
  params_b: number | null;
}

export interface EvalRun {
  id: number;
  task_id: number;
  model_id: number;
  status: "QUEUED" | "RUNNING" | "COMPLETE" | "FAILED";
  metrics: Record<string, any> | null;
  artifacts_dir: string | null;
  command: string | null;
  git_commit: string | null;
  error: string | null;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
  task_name?: string;
  model_name?: string;
  model_display_name?: string;
  primary_metric?: number;
  duration_seconds?: number;
}

export interface LeaderboardEntry {
  model_name: string;
  model_display_name: string;
  primary_metric: number | null;
  run_id: number;
  run_date: string;
  git_commit: string | null;
  status: string;
}

export interface ChatMessage {
  role: "user" | "assistant";
  content: string;
  image_url?: string;
}

export interface Image {
  id: number;
  filename: string;
  url: string;
  is_public: boolean;
  created_at: string;
  user_id: number;
}

// Adaptations
export interface UserAdaptation {
  id: number;
  user_id: number;
  base_model_id: number;
  level: "rag" | "soft_prompt" | "lora"| "full";
  status: "pending" | "training" | "ready" | "failed";
  artifact_path: string | null;
  config: Record<string, any> | null;
  metrics: Record<string, any> | null;
  contribution_count: number;
  created_at: string;
  last_trained_at: string | null;
}

// A model you can pick (GET /api/models)
export interface ServedModel {
    alias: string;               // what to ask for
    version: string;             // exact model that answers
    display_name: string | null;
    kind: "base" | "lora" | "full" | "prompt" | "rag";
    base_model: string | null;
    personal: boolean;           // trained for you only
  }

export interface ComparisonResponse {
  image_id: number;
  prompt: string;
  base_response: string;
  adapted_response: string;
  model?: string | null;              // alias that wrote adapted_response
  adaptation_level?: string | null;
  adaptation_id?: number | null;
  base_model_version?: string;
  adapted_model_version?: string;
  adapted_context?: RagContextItem[] | null;   // corrections a RAG model used (null: not RAG)
}

export interface ComparisonFeedback {
  id: number;
  user_id: number;
  image_id: number;
  adaptation_id: number | null;
  prompt: string;
  base_response?: string;      // optional, for samples page
  adapted_response?: string;   // optional, for samples page
  preference: string | null;
  base_thumbs: string | null;
  adapted_thumbs: string | null;
  created_at: string;
}

// One of your earlier corrections that a RAG model used for its answer
export interface RagContextItem {
  example_id: string;
  image_id: number | null;
  kind: string;
  question: string;
  same_image: boolean;
  score: number | null;        // reranker relevance, 0-1
  summary?: string;
  sources?: string[] | null;   // refined entries: the feedback records they were written from
}

// Signup / admin
export interface SignupData {
  email: string;
  password: string;
  invite_code?: string;
  note?: string;
}

export interface SignupResult {
  id: number;
  email: string;
  status: "active" | "pending";
}

export interface AdminSignup {
  id: number;
  email: string;
  signup_note: string | null;
  created_at: string;
  is_active: boolean;
  approved_at: string | null;
  approval_notified_at: string | null;
}

export type InviteStatus = "unused" | "used" | "expired" | "revoked";

export interface AdminInvite {
  id: number;
  note: string | null;
  created_at: string;
  expires_at: string | null;
  used_at: string | null;
  used_by_email: string | null;
  revoked_at: string | null;
  status: InviteStatus;
}

export interface AdminInviteCreated extends AdminInvite {
  code: string;
}

// FastAPI errors are a string, or a list of validation errors for 422s
function errorMessage(detail: unknown, fallback: string): string {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => (d && typeof d === "object" && "msg" in d ? String(d.msg) : String(d))).join("; ");
  }
  return fallback;
}

async function jsonOrThrow<T>(response: Response, fallback: string): Promise<T> {
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(errorMessage(error.detail, fallback));
  }
  return response.json();
}


// Token management
let accessToken: string | null = null;

export function setAccessToken(token: string | null) {
  accessToken = token;
  if (token) {
    localStorage.setItem("access_token", token);
  } else {
    localStorage.removeItem("access_token");
  }
}

export function getAccessToken(): string | null {
  if (accessToken) return accessToken;
  if (typeof window !== "undefined") {
    accessToken = localStorage.getItem("access_token");
  }
  return accessToken;
}




// Base fetch with auth
async function fetchWithAuth(
  endpoint: string,
  options: RequestInit = {}
): Promise<Response> {
  const token = getAccessToken();
  const headers: HeadersInit = {
    "Content-Type": "application/json",
    ...options.headers,
  };

  if (token) {
    (headers as Record<string, string>)["Authorization"] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers,
  });

  // Handle 401 - clear token and redirect
  if (response.status === 401) {
    setAccessToken(null);
    if (typeof window !== "undefined" && !window.location.pathname.includes('/login')) {
      window.location.href = "/login";
    }
  }

  return response;
}

// API functions
export const api = {
  // Auth - Updated to match your FastAPI endpoints
  async login(email: string, password: string): Promise<{ access_token: string; user: User }> {
    const formData = new URLSearchParams();
    formData.append('username', email);
    formData.append('password', password);

    const response = await fetch(`${API_BASE}/auth/token`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || "Login failed");
    }

    const data = await response.json();
    setAccessToken(data.access_token);

    // User data now comes from backend response
    return { 
      access_token: data.access_token, 
      user: data.user 
    };  
  },


  async register(data: SignupData): Promise<SignupResult> {
    const response = await fetch(`${API_BASE}/auth/signup`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(errorMessage(error.detail, "Registration failed"));
    }
    return response.json();
  },

  async getCurrentUser(): Promise<User> {
    const response = await fetchWithAuth("/auth/me");
  
    if (!response.ok) {
      setAccessToken(null);
      throw new Error("Not authenticated");
    }
    
    return response.json();
  },

  logout() {
    setAccessToken(null);
    fetch(`${API_BASE}/auth/logout`, { method: "POST" }).catch(() => {});
  },



  // Tasks
  async getTasks(): Promise<Task[]> {
    const response = await fetchWithAuth("/api/evals/tasks");
    if (!response.ok) throw new Error("Failed to fetch tasks");
    return response.json();
  },

  async getTask(name: string): Promise<Task> {
    const response = await fetchWithAuth(`/api/evals/tasks/${name}`);
    if (!response.ok) throw new Error("Failed to fetch task");
    return response.json();
  },


  async getAvailableMetrics(taskName: string): Promise<string[]> {
    const response = await fetchWithAuth(`/api/evals/tasks/${taskName}/metrics`);
    if (!response.ok) throw new Error("Failed to fetch metrics");
    return response.json();
  },

  async getLeaderboard(taskName: string, metric?: string): Promise<LeaderboardEntry[]> {
    const params = new URLSearchParams();
    if (metric) params.set("metric", metric);
    const response = await fetchWithAuth(`/api/evals/tasks/${taskName}/leaderboard?${params}`);
    if (!response.ok) throw new Error("Failed to fetch leaderboard");
    return response.json();
  },


  async getTaskRuns(taskName: string, status?: string): Promise<EvalRun[]> {
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    const response = await fetchWithAuth(`/api/evals/tasks/${taskName}/runs?${params}`);
    if (!response.ok) throw new Error("Failed to fetch runs");
    return response.json();
  },

  async triggerEval(taskName: string, modelName: string): Promise<EvalRun> {
    const response = await fetchWithAuth(`/api/evals/tasks/${taskName}/runs`, {
      method: "POST",
      body: JSON.stringify({ model_name: modelName }),
    });
    if (!response.ok) throw new Error("Failed to trigger evaluation");
    return response.json();
  },

  // Models
  async getModels(): Promise<Model[]> {
    const response = await fetchWithAuth("/api/evals/models");
    if (!response.ok) throw new Error("Failed to fetch models");
    return response.json();
  },

  // Runs
  async getRun(runId: number): Promise<EvalRun> {
    const response = await fetchWithAuth(`/api/evals/runs/${runId}`);
    if (!response.ok) throw new Error("Failed to fetch run");
    return response.json();
  },

  // Chat
  async sendMessage(
    messages: ChatMessage[],
    imageUrl?: string
  ): Promise<ReadableStream<Uint8Array>> {
    const response = await fetchWithAuth("/api/chat", {
      method: "POST",
      body: JSON.stringify({ messages, image_url: imageUrl }),
    });
    if (!response.ok) throw new Error("Chat request failed");
    return response.body!;
  },

  // Images
  async getImages(options?: {
    public?: boolean,
    limit?: number; 
    offset?: number;
  }): Promise<Image[]> {
    const params = new URLSearchParams();
 
    if (options?.public !== undefined) params.set("public", String(options.public));
    if (options?.limit !== undefined) params.set("limit", String(options.limit));
    if (options?.offset !== undefined) params.set("offset", String(options.offset));

    console.log("Fetching images with params:", params.toString());  // Add this

    
    const response = await fetchWithAuth(`/images/me?${params}`);
    //const images = await response.json();    
    if (!response.ok) throw new Error("Failed to fetch images");
    return response.json();
    //return images;
  },

  async uploadImage(file: File, isPublic: boolean): Promise<Image> {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("is_public", String(isPublic));

    const token = getAccessToken();
    //const response = await fetch(`${API_BASE}/api/images/upload`, {
    const response = await fetch(`/images/ingest_upload`, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });
    if (!response.ok) throw new Error("Failed to upload image");
    return response.json();
  },

  async toggleImageVisibility(imageId: number, isPublic: boolean): Promise<Image> {
    //const response = await fetchWithAuth(`/api/images/${imageId}`, {
    const response = await fetchWithAuth(`/images/${imageId}`, {
      method: "PATCH",
      body: JSON.stringify({ is_public: isPublic }),
    });
    if (!response.ok) throw new Error("Failed to update image");
    return response.json();
  },

  async getChatFeedbackConfig(): Promise<FeedbackConfig> {
    const response = await fetchWithAuth("/api/chat-feedback/config");
    if (!response.ok) throw new Error("Failed to get feedback config");
    return response.json();
  },

  async submitChatFeedback(data: {
    chat_session_id?: number;
    turn_index: number;
    thumbs?: string;
    edit_original?: string;
    edit_revised?: string;
  }): Promise<ChatFeedback> {
    const response = await fetchWithAuth("/api/chat-feedback/", {
      method: "POST",
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || "Failed to submit feedback");
    }
    return response.json();
  },

  async submitFeedback(data: {
    chat_session_id?: number;
    messages?: { role: string; content: string }[];
    title?: string;
    turn_index: number;
    thumbs?: string;
    edit_original?: string;
    edit_revised?: string;
    comment_text?: string;
    image_id?: number;
    model_name?: string;
  }): Promise<{ chat_session_id: number; feedback_id?: number; comment_id?: number }> {
    const response = await fetchWithAuth("/api/chat-feedback/submit", {
      method: "POST",
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || "Failed to submit feedback");
    }
    return response.json();
  },

  async submitChatComment(data: {
    chat_session_id?: number;
    turn_index: number;
    text: string;
  }): Promise<ChatComment> {
    const response = await fetchWithAuth("/api/chat-feedback/comments", {
      method: "POST",
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || "Failed to submit comment");
    }
    return response.json();
  },

  // Adaptations
  async getAdaptations(options?: {
    level?: string;
    status?: string;
  }): Promise<UserAdaptation[]> {
    const params = new URLSearchParams();
    if (options?.level) params.set("level", options.level);
    if (options?.status) params.set("status", options.status);
    const response = await fetchWithAuth(`/api/adaptations/?${params}`);
    if (!response.ok) throw new Error("Failed to fetch adaptations");
    return response.json();
  },

  async triggerAdaptation(data: {
    level: "rag" | "soft_prompt" | "lora" | "full";
    base_model_id: number;
    config?: Record<string, any>;
  }): Promise<UserAdaptation> {
    const response = await fetchWithAuth("/api/adaptations/trigger", {
      method: "POST",
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || "Failed to trigger adaptation");
    }
    return response.json();
  },

  async deleteAdaptation(adaptationId: number): Promise<void> {
    const response = await fetchWithAuth(`/api/adaptations/${adaptationId}`, {
      method: "DELETE",
    });
    if (!response.ok) throw new Error("Failed to delete adaptation");
  },

  // Models you can use in Compare: shared ones plus your personal ones
     async getServedModels(): Promise<ServedModel[]> {
       const response = await fetchWithAuth("/api/models/");
       if (!response.ok) throw new Error("Failed to fetch models");
       return response.json();
     },

  // Comparison
  async runComparison(data: {
    image_id: number;
    prompt: string;
    model?: string;
    adaptation_id?: number;
  }): Promise<ComparisonResponse> {
    const response = await fetchWithAuth("/api/comparison/run", {
      method: "POST",
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || "Failed to run comparison");
    }
    return response.json();
  },

  async submitComparisonFeedback(data: {
    image_id: number;
    adaptation_id?: number;
    prompt: string;
    base_response: string;
    adapted_response: string;
    base_model_version?: string;
    adapted_model_version?: string;
    preference?: string;
    base_thumbs?: string;
    adapted_thumbs?: string;
    base_edit?: string;
    adapted_edit?: string;
    comment?: string;
  }): Promise<ComparisonFeedback> {
    const response = await fetchWithAuth("/api/comparison/feedback", {
      method: "POST",
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || "Failed to submit feedback");
    }
    return response.json();
  },

  async getComparisonFeedback(adaptationId?: number): Promise<ComparisonFeedback[]> {
    const params = adaptationId ? `?adaptation_id=${adaptationId}` : "";
    const response = await fetchWithAuth(`/api/comparison/feedback${params}`);
    if (!response.ok) throw new Error("Failed to get comparison feedback");
    return response.json();
  },

  // Admin: signups
  async adminListSignups(status: "pending" | "unnotified" | "approved" | "all" = "pending"): Promise<AdminSignup[]> {
    const response = await fetchWithAuth(`/api/admin/signups?status=${status}`);
    return jsonOrThrow(response, "Failed to load signups");
  },

  async adminApproveSignup(userId: number): Promise<AdminSignup> {
    const response = await fetchWithAuth(`/api/admin/signups/${userId}/approve`, { method: "POST" });
    return jsonOrThrow(response, "Failed to approve");
  },

  async adminRejectSignup(userId: number): Promise<void> {
    const response = await fetchWithAuth(`/api/admin/signups/${userId}/reject`, { method: "POST" });
    await jsonOrThrow(response, "Failed to reject");
  },

  // Admin: approval-email export
  async adminDownloadApprovalExport(userIds: number[]): Promise<void> {
    const params = new URLSearchParams();
    userIds.forEach((id) => params.append("user_ids", String(id)));
    const response = await fetchWithAuth(`/api/admin/approvals/export?${params}`);
    if (!response.ok) throw new Error("Failed to export approval emails");
    const text = await response.text();
    const match = /filename="([^"]+)"/.exec(response.headers.get("Content-Disposition") || "");
    const url = URL.createObjectURL(new Blob([text], { type: "text/plain" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = match?.[1] ?? "approval-emails.txt";
    a.click();
    URL.revokeObjectURL(url);
  },

  async adminMarkNotified(userIds: number[]): Promise<{ ok: boolean; marked: number }> {
    const response = await fetchWithAuth("/api/admin/approvals/mark-notified", {
      method: "POST",
      body: JSON.stringify({ user_ids: userIds }),
    });
    return jsonOrThrow(response, "Failed to mark as sent");
  },

  // Admin: invite codes
  async adminListInvites(): Promise<AdminInvite[]> {
    const response = await fetchWithAuth("/api/admin/invites");
    return jsonOrThrow(response, "Failed to load invites");
  },

  async adminCreateInvite(data: { note?: string; expires_in_days?: number | null }): Promise<AdminInviteCreated> {
    const response = await fetchWithAuth("/api/admin/invites", {
      method: "POST",
      body: JSON.stringify(data),
    });
    return jsonOrThrow(response, "Failed to create invite");
  },

  async adminRevokeInvite(inviteId: number): Promise<AdminInvite> {
    const response = await fetchWithAuth(`/api/admin/invites/${inviteId}/revoke`, { method: "POST" });
    return jsonOrThrow(response, "Failed to revoke invite");
  },

};
